import React from "react";
import PropTypes from "prop-types";
import cname from "./style.module.scss";
import { ControlButtons } from "../../ControlTooltip";
import { useSpectrophotometer } from "./Spectrophotometer.utils";
import { SpectrophotometerImage } from "./SpectrophotometerImages";
import { SpectrophotometerTooltip } from "./SpectrophotometerTooltip";
import { SpectrophotometerInfoModal } from "./SpectrophotometerInfoModal";
import { SpectrophotometerConfigModal } from "./SpectrophotometerConfigModal";

/**
 * Spectrophotometer:
 * - Lab Apparatus to measure light
 */
export function Spectrophotometer(props) {
  const { getButtonList } = useSpectrophotometer();

  const [hover, setHover] = React.useState(false);

  const toggleHover = (value) => () => setHover(value);

  const toggleLid = (value) => () => props.toggleLid(value);

  const toggleInfoModal = (value) => () => props.toggleInfoModal(value);

  const toggleSettingsModal = (value) => () => props.toggleSettingsModal(value);

  const renderInfomodal = () =>
    props.showInfoModal && (
      <SpectrophotometerInfoModal
        notebookCollapsed={props.notebookCollapsed}
        isOpen={props.showInfoModal}
        onClose={toggleInfoModal(false)}
      />
    );

  const renderConfigModal = () =>
    props.showSettingsModal && (
      <SpectrophotometerConfigModal
        notebookCollapsed={props.notebookCollapsed}
        poweredUp={props.poweredUp}
        wavelength={props.wavelength}
        isOpen={props.showSettingsModal}
        setWavelength={props.setWavelength}
        questionTableCellUpdateResults={props.questionTableCellUpdateResults}
        absorbanceTableCellUpdate={props.absorbanceTableCellUpdate}
        absorbanceTable={props.absorbanceTable}
        onClose={toggleSettingsModal(false)}
        absorbanceValue={props.absorbanceValue}
        onClickZeroButton={props.onClickZeroButton}
        relativeAbsorbance={props.relativeAbsorbance}
        onClickPowerButton={props.onClickPowerButton}
        cuvetteId={props.cuvetteId}
      />
    );

  return (
    <>
      <div
        className={cname.spectrophotometer}
        onMouseEnter={toggleHover(true)}
        onMouseLeave={toggleHover(false)}
      >
        <SpectrophotometerTooltip show={hover} lidOpen={props.lidOpen}>
          <ControlButtons
            buttonList={getButtonList({
              toggleLid: toggleLid,
              lidOpen: props.lidOpen,
              cuvetteId: props.cuvetteId,
              removeCuvette: props.removeCuvette,
              showInfoModal: props.showInfoModal,
              openInfoModal: toggleInfoModal(true),
              showSettingsModal: props.showSettingsModal,
              toggleSettingsModal: toggleSettingsModal(true),
            })}
          />
        </SpectrophotometerTooltip>
        <SpectrophotometerImage
          absorbanceValue={props.absorbanceValue}
          relativeAbsorbance={props.relativeAbsorbance}
          absorbance={props.absorbanceValue}
          poweredUp={props.poweredUp}
          solution={props.solution}
          cuvetteId={props.cuvetteId}
          lidOpen={props.lidOpen}
          className={cname.image}
        />
      </div>
      {renderInfomodal()}
      {renderConfigModal()}
    </>
  );
}

Spectrophotometer.propTypes = {
  /**
   * UniqueId for the cuvette placed in spectrophotometer
   */
  cuvetteId: PropTypes.number,
  /**
   * Callback for remove Cuvette Icon
   */
  removeCuvette: PropTypes.func,
  /**
   * Booleen set if Spectrophotometer is open
   */
  lidOpen: PropTypes.bool,
  /**
   * Callback to toggle lid open/close
   */
  toggleLid: PropTypes.func,
  /**
   * Boolean set to show/hide info modal
   */
  showInfoModal: PropTypes.bool,
  /**
   * Callback to show/hide infoModal
   */
  toggleInfoModal: PropTypes.func,
  /**
   * Booelan set to show/hide settings modal
   */
  showSettingsModal: PropTypes.bool,
  /**
   * Callback to show/hide settings modal
   */
  toggleSettingsModal: PropTypes.func,
  /**
   * Absorbance value of solution inside the cuvette
   */
  absorbanceValue: PropTypes.number,
  /**
   * Absorbance value for the solvent set to calculate
   * the absorbanceValue of solution.
   */
  relativeAbsorbance: PropTypes.number,
  /**
   * Boolean to set if spectrophotometer is on/off
   */
  poweredUp: PropTypes.bool,
  /**
   * Callback to powerUp/Down the device
   */
  onClickPowerButton: PropTypes.func,
  /**
   * Callback to set the relative absorbanceValue
   */
  onClickZeroButton: PropTypes.func,
  /**
   * Current wavelenght
   */
  wavelength: PropTypes.number,
  /**
   * Callback function to set wavelength value
   */
  setWavelength: PropTypes.func,
  /**
   * Hex  Color value for solution placed in spectrophotometer
   */
  solution: PropTypes.string,
};

Spectrophotometer.defaultProps = {
  cuvetteId: undefined,
  lidOpen: false,
  showInfoModal: false,
  showSettingsModal: false,
  poweredUp: false,
  solution: "",
};
